import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { HelpCircle, LogOut, Settings, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/lib/auth.jsx";
import { HelpFAB } from "@/components/HelpFAB";

const ROLE_COLORS = {
  admin: "bg-indigo-600 text-white",
  builder: "bg-emerald-100 text-emerald-700",
  presenter: "bg-amber-100 text-amber-700",
  viewer: "bg-slate-100 text-slate-600",
};

export function Layout() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasPermission("users.manage");

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header
        data-testid="app-topbar"
        className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3">
          <Link
            to="/workflows"
            data-testid="brand-link"
            className="group flex items-center gap-3"
          >
            <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-lg bg-white ring-1 ring-slate-200 shadow-sm transition-transform group-hover:scale-105">
              <img
                src="/eyvo-logo.jpg"
                alt="Eyvo"
                className="h-10 w-10 object-contain"
                data-testid="brand-logo"
              />
            </div>
            <div className="leading-tight">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Demo Agent
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                Workflow Library
              </div>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            <TopLink to="/workflows" testid="nav-workflows">
              Workflows
            </TopLink>
            <TopLink to="/runs" testid="nav-runs">
              Demo Runs
            </TopLink>
            <TopLink to="/history" testid="nav-history">
              History
            </TopLink>
            <TopLink to="/folders" testid="nav-folders">
              Folders
            </TopLink>
            {isAdmin && (
              <TopLink to="/shares" testid="nav-shares">
                Shares
              </TopLink>
            )}
            {isAdmin && (
              <TopLink to="/users" testid="nav-users">
                Users
              </TopLink>
            )}
            {isAdmin && (
              <TopLink to="/admin/email-delivery" testid="nav-admin-email-delivery">
                Email
              </TopLink>
            )}
            <NavLink
              to="/help"
              data-testid="nav-help"
              aria-label="Open Help Center"
              title="Help Center"
              className={({ isActive }) =>
                [
                  "ml-1 grid h-8 w-8 place-items-center rounded-full transition-colors",
                  isActive
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900",
                ].join(" ")
              }
            >
              <HelpCircle size={17} strokeWidth={2} />
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <div
              className="hidden items-center gap-3 md:flex"
              data-testid="topbar-user"
            >
              <Avatar src={user?.picture} name={user?.name || user?.email} />
              <div className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs font-semibold text-slate-700">
                    {user?.name || user?.email}
                  </span>
                  <RolePill role={user?.role} />
                </div>
                <div className="text-[10px] text-slate-400">{user?.email}</div>
              </div>
            </div>
            <Separator orientation="vertical" className="hidden h-8 md:block" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-600"
            >
              <LogOut size={16} className="mr-1.5" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-6 py-8" data-testid="app-content">
        <Outlet />
      </main>
      <HelpFAB />
    </div>
  );
}

function TopLink({ to, children, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        [
          "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          isActive
            ? "bg-indigo-50 text-indigo-700"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}

function RolePill({ role }) {
  if (!role) return null;
  const cls = ROLE_COLORS[role] || ROLE_COLORS.viewer;
  return (
    <span
      data-testid={`role-pill-${role}`}
      className={[
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        cls,
      ].join(" ")}
    >
      {role}
    </span>
  );
}

function Avatar({ src, name }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        data-testid="topbar-avatar"
        referrerPolicy="no-referrer"
        className="h-8 w-8 rounded-full border border-slate-200 object-cover"
      />
    );
  }
  const initials = (name || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      data-testid="topbar-avatar"
      className="grid h-8 w-8 place-items-center rounded-full bg-indigo-100 text-[11px] font-semibold text-indigo-700"
    >
      {initials}
    </div>
  );
}
